import { MappingContext } from '@app/mapping/MappingContext';
import { FilterSpecification } from 'mapbox-gl';
import { FC, JSX, useCallback, useContext, useMemo, useState } from 'react';
import * as ReactMapGL from "react-map-gl/mapbox";
import { MapboxSupportAlert } from '@app/shared/mapping/MapboxSupportAlert';
import { Layer, NavigationControl, Source } from "react-map-gl/mapbox";
import { GapDensityMapDataContext, GapDensityMapDataContextProvider, getBuckets } from '@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapDataContext';
import { LoadingSpinnerControl } from '@app/shared/mapping/LoadingSpinnerControl';
import { GAP_DENSITY_MAP_STYLE } from './GapDensityMapsConstants';
import { MapPropertyKeysEnum } from '@app/projects/network-assessment/maps/mapPropertyKeysEnum';
import { GapDensityMapCountyInfoPopup } from '@app/projects/network-assessment/maps/gap-density-map/components/GapDensityMapCountyInfoPopup';
import GapDensityMapLegendControl from './controls/GapDensityMapLegendControl';
import type { ExpressionSpecification } from 'mapbox-gl';
import { FilterType } from '@app/projects/enumerators';
import { FilterContextProvider } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext';
import GapDensityMapThemeControl from '@app/projects/network-assessment/maps/gap-density-map/controls/GapDensityMapThemeControl';
import { GapDensityMapThemeContext, GapDensityMapThemeContextProvider } from '@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapThemeContext';
import MapExport from '@app/projects/network-assessment/maps/gap-density-map/MapExport';
import { CountyProjectContext } from '@app/projects/CountyProjectContext';
import { QESAlert } from '@app/shared/qes-standards/Alert';

const WrappedGapDensityMap = (): JSX.Element => {
  // Contexts
  const mappingContext = useContext(MappingContext);
  const mapDataContext = useContext(GapDensityMapDataContext);
  const themeContext = useContext(GapDensityMapThemeContext);
  const projectContext = useContext(CountyProjectContext);

  const [viewState, setViewState] = useState<ReactMapGL.ViewState>(mappingContext.defaultViewState);

  const onMapLoad = useCallback((): void => {

    const map = mapDataContext.mapRef.current?.getMap();

    if (!map) return;

    mapDataContext.mapRef.current.setCenter([mappingContext.defaultLongitude, mappingContext.defaultLatitude]);

    mapDataContext.mapRef.current.on('mousemove', GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_LAYER_ID, (e) => mapDataContext.handleMouseMoveOnCounty(e));
    mapDataContext.mapRef.current.on('mouseleave', GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_LAYER_ID, () => mapDataContext.handleMouseLeaveOnCounty());
    mapDataContext.mapRef.current.on('click', GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_LAYER_ID, (e) => mapDataContext.handleMouseClickOnCounty(e));
  }, []);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: mappingContext.defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: mappingContext.defaultPitch
    });
  }, []);

  const getFilter = (): FilterSpecification => {
    return ['all',
      !!mapDataContext.countyMapFilters ? ['in', ['get', MapPropertyKeysEnum.CountySsa], ["literal", mapDataContext.countyMapFilters]] : true
    ];
  }

  const selectedSpecialty = mapDataContext.filters?.find(f => f.filterType === FilterType.Specialty)?.filterValue;

  const showInformationalAlert = useMemo(() => {
    if (!selectedSpecialty || !projectContext?.runConfiguration?.specialtyGroups) {
      return false;
    }
    const selectedSpecialtyGroup = projectContext.runConfiguration.specialtyGroups.find(sg => sg.code === selectedSpecialty);

    return selectedSpecialtyGroup?.isInformational;
  }, [selectedSpecialty, projectContext?.runConfiguration?.specialtyGroups]);

  const getColorExpression = useMemo(() => {
    if (!selectedSpecialty) {
      const specialtiesCount = mapDataContext.countyMaps?.features?.[0]?.properties?.specialties?.length || 0;
      let ranges: number[][] = [];
      if (specialtiesCount > 0) {
        ranges = getBuckets(specialtiesCount);
      }

      const expression: ExpressionSpecification = ['case'];

      const getGapColor = (idx: number): string => {
        const key = `gapColor${idx}` as keyof typeof themeContext.currentTheme;
        return themeContext.currentTheme[key];
      };

      expression.push(['==', ['get', MapPropertyKeysEnum.NumberOfSpecialtyCodeWithGaps], 0], themeContext.currentTheme.noGapColor);

      ranges.slice(1).forEach((range, index) => {
        expression.push(
          ['all', ['>=', ['get', MapPropertyKeysEnum.NumberOfSpecialtyCodeWithGaps], range[0]], ['<=', ['get', MapPropertyKeysEnum.NumberOfSpecialtyCodeWithGaps], range[1]]],
          getGapColor(index + 1)
        )
      });

      expression.push(themeContext.currentTheme.noGapColor); // Default case if no match

      return expression;
    }
    // Specialty-level coloring
    return [
      'case',
      [
        'in',
        selectedSpecialty,
        ['get', 'specialtyCodeWithGaps']
      ],
      themeContext.currentTheme.gapColor4, // color for gap
      themeContext.currentTheme.noGapColor // color for no gap
    ] as ExpressionSpecification;
  }, [mapDataContext.countyMaps, mapDataContext.filters, themeContext.currentTheme]);

  return (
    <>
      {showInformationalAlert && (
        <QESAlert type="info" persist={true} classNames="mb-3">
          This map doesn&apos;t support Informational Specialties.
        </QESAlert>
      )}
      <div className="gap-density-map__map-export-container">
        <MapExport
          isLoading={mapDataContext.isLoading && !!projectContext?.runConfiguration}
        />
      </div>
      <div className="gap-density-map__map-container">
        <div className='gap-density-map__map'>
          <ReactMapGL.Map
            {...viewState}
            {...mappingContext.defaultMapProps}
            onLoad={onMapLoad}
            onMove={(e) => onMapMove(e.viewState)}
            preserveDrawingBuffer={true}
            ref={mapDataContext.mapRef}
          >
            <NavigationControl data-html2canvas-ignore="true" position="top-right" showCompass={false} />
            <LoadingSpinnerControl mapRef={mapDataContext.mapRef} ignoreInMapExport={true} position="top-left" isLoading={mapDataContext.isLoading} />
            <Source
              id={GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_SOURCE_ID}
              type="geojson"
              data={mapDataContext.countyMaps}
            >
              {/* County fill layer */}
              <Layer
                id={GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_LAYER_ID}
                type="fill"
                paint={{
                  "fill-color": getColorExpression,
                  "fill-opacity": 0.58
                }}
                filter={getFilter()}
              />

              {/* County outline layer. */}
              <Layer
                id={GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_OUTLINE_LAYER_ID}
                type="line"
                paint={{
                  "line-color": [
                    'case',
                    ['==', ['get', MapPropertyKeysEnum.CountySsa], !!mapDataContext.selectedCountyFeature ? mapDataContext.selectedCountyFeature.properties.countySSA : null],
                    GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_SELECTED_OUTLINE_COLOR,
                    GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_OUTLINE_COLOR
                  ],
                  "line-offset": 0.5,
                  "line-width": [
                    'case',
                    ['==', ['get', MapPropertyKeysEnum.CountySsa], !!mapDataContext.selectedCountyFeature ? mapDataContext.selectedCountyFeature.properties.countySSA : null],
                    GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_SELECTED_OUTLINE_WIDTH,
                    GAP_DENSITY_MAP_STYLE.COUNTY_SERVICING_AREA_OUTLINE_WIDTH,
                  ],
                }}
                filter={getFilter()}
              />
            </Source>
            { /* Hover box which follows the mouse */}
            <GapDensityMapCountyInfoPopup isCountySelectionPopup={false} />
            { /* Popup box which is centered over the selected county */}
            <GapDensityMapCountyInfoPopup isCountySelectionPopup={true} />
            {/* Legend */}
            <GapDensityMapLegendControl position="bottom-right" ignoreInMapExport={false} />
            {/* Theme control */}
            <GapDensityMapThemeControl position="top-right" ignoreInMapExport={true} />
          </ReactMapGL.Map>
        </div>
      </div>
    </>
  );
}

export const GapDensityMap: FC = () => (
  <FilterContextProvider>
    <GapDensityMapDataContextProvider>
      <GapDensityMapThemeContextProvider>
        <MapboxSupportAlert>
          <WrappedGapDensityMap />
        </MapboxSupportAlert>
      </GapDensityMapThemeContextProvider>
    </GapDensityMapDataContextProvider>
  </FilterContextProvider>
);